import { FilesUploader } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { DirectUploadMedia } from "libs/directUpload";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, FormField, Input, MediaFilePicker } from "ui";

import { EMPTY_DELIVERY_COMPANY_FORM_VALUES } from "../../../constants";
import {
  DeliveryCompany,
  DeliveryCompanyFormValues
} from "../../../deliveryCompanies.types";

import styles from "./CompanyFormModal.module.scss";

const COMPANY_FORM_ID = "COMPANY_FORM_ID";

interface CompanyFormModalProps {
  isOpen: boolean;
  editableCompany?: DeliveryCompany | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (values: DeliveryCompanyFormValues) => void;
}

const CompanyFormModal = ({
  isOpen,
  editableCompany,
  isSubmitting,
  onClose,
  onSubmit
}: CompanyFormModalProps) => {
  const { t } = useTranslation();

  const isEdit = !!editableCompany;

  const defaultValues: DeliveryCompanyFormValues = editableCompany
    ? {
        name: editableCompany.name,
        email: editableCompany.email,
        password: "",
        repeatPassword: "",
        capacity: editableCompany.capacity,
        phoneNumber: editableCompany.phoneNumber ?? "",
        address: editableCompany.address ?? "",
        avatarMediaId: editableCompany.avatar?.id ?? null
      }
    : EMPTY_DELIVERY_COMPANY_FORM_VALUES;

  const form = useForm<DeliveryCompanyFormValues>({ defaultValues });

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = form;

  const passwordValue = watch("password");

  const initialAvatar: DirectUploadMedia | null = editableCompany?.avatar
    ? (editableCompany.avatar as unknown as DirectUploadMedia)
    : null;

  const [avatarMedia, setAvatarMedia] = useState<DirectUploadMedia | null>(
    initialAvatar
  );

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      setAvatarMedia(initialAvatar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editableCompany?.id]);

  const onAvatarUploaded = (media: DirectUploadMedia) => {
    setAvatarMedia(media);
    setValue("avatarMediaId", media.id);
  };

  const onAvatarRemoved = () => {
    setAvatarMedia(null);
    setValue("avatarMediaId", null);
  };

  return (
    <FormProvider {...form}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? "Edit Company" : "New Company"}
        formId={COMPANY_FORM_ID}
        okText={isEdit ? "Save changes" : "Create company"}
        cancelText="Cancel"
        isLoading={isSubmitting}
        className={styles.companyForm__modal}
      >
        <form
          id={COMPANY_FORM_ID}
          onSubmit={handleSubmit((values) =>
            onSubmit({
              ...values,
              capacity: values.capacity === "" ? 0 : Number(values.capacity)
            })
          )}
          className={styles.companyForm}
        >
          <Controller
            control={control}
            name="avatarMediaId"
            render={() => (
              <div className={styles.companyForm__avatarBlock}>
                <FilesUploader
                  onSuccessUploading={onAvatarUploaded}
                  uploadDirectoryKey={MediaDirectoryKeys.DeliveryCompany}
                >
                  {(uploaderProps) => (
                    <MediaFilePicker
                      allowedMediaTypes={[MediaType.IMAGE]}
                      size="md"
                      variant="rectangle"
                      customSize={{ width: 100, height: 100 }}
                      {...uploaderProps}
                      value={avatarMedia}
                      onDeleteMediaFile={onAvatarRemoved}
                      data-testid="company--avatar"
                    />
                  )}
                </FilesUploader>
                <span className={styles.companyForm__avatarHint}>
                  {t("Change photo")}
                </span>
              </div>
            )}
          />

          <div className={styles.companyForm__line} />

          <div className={styles.companyForm__row}>
            <FormField
              label={t("Company Name")}
              errorMessage={errors.name?.message}
            >
              <Input
                {...register("name", {
                  required: t("Required field")
                })}
                placeholder={t("Yango Express")}
              />
            </FormField>

            <FormField
              label={t("Contact Phone")}
              errorMessage={errors.phoneNumber?.message}
            >
              <Input
                {...register("phoneNumber")}
                placeholder="+225 27 22 12 34 56"
              />
            </FormField>
          </div>

          <div className={styles.companyForm__row}>
            <FormField label={t("Email")} errorMessage={errors.email?.message}>
              <Input
                type="email"
                {...register("email", {
                  required: t("Required field")
                })}
                placeholder="company@example.com"
              />
            </FormField>

            <FormField
              label={t("Capacity")}
              errorMessage={errors.capacity?.message}
            >
              <Input
                type="number"
                min={0}
                {...register("capacity", {
                  required: t("Required field"),
                  valueAsNumber: true
                })}
                placeholder="50"
              />
              <span className={styles.companyForm__hint}>
                {t("Max orders per cycle")}
              </span>
            </FormField>
          </div>

          <FormField
            label={t("Address")}
            errorMessage={errors.address?.message}
          >
            <Input
              {...register("address")}
              placeholder={t("Plateau, Abidjan")}
            />
          </FormField>

          <div className={styles.companyForm__row}>
            <FormField
              label={isEdit ? t("New Password") : t("Password")}
              errorMessage={errors.password?.message}
            >
              <Input
                isPassword
                {...register("password", {
                  required: isEdit ? false : t("Required field"),
                  minLength: passwordValue
                    ? { value: 8, message: t("Min 8 characters") }
                    : undefined
                })}
                placeholder={
                  isEdit ? t("Leave empty to keep current") : "••••••••"
                }
              />
            </FormField>

            <FormField
              label={t("Repeat Password")}
              errorMessage={errors.repeatPassword?.message}
            >
              <Input
                isPassword
                {...register("repeatPassword", {
                  required:
                    !isEdit || passwordValue ? t("Required field") : false,
                  validate: (value) =>
                    !passwordValue ||
                    value === passwordValue ||
                    t("Passwords do not match")
                })}
                placeholder="••••••••"
              />
            </FormField>
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
};

export default CompanyFormModal;
